import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import { StreakInline } from "@/components/mnf/StreakPill";
import SeasonChip from "@/components/mnf/SeasonChip";
import { useStoreQuery, queries, mutations } from "@/lib/store";

type Status = "thriving" | "steady" | "needs love";
type Tier = "Seedling" | "Rooted" | "Flourishing";

interface Sister {
  name: string;
  slug: string;
  initials: string;
  tier: Tier;
  lastCheckIn: string;
  streak: number;
  resting: boolean;
  restingDays: number;
  status: Status;
  ringColor: string;
}

const SISTERS: Sister[] = [
  { name: "Rebecca Hayes",  slug: "rebecca", initials: "RH", tier: "Flourishing", lastCheckIn: "2h ago",  streak: 38, resting: false, restingDays: 0, status: "thriving",  ringColor: "rgba(192,120,74,0.55)" },
  { name: "Joanna Pierce",  slug: "joanna",  initials: "JP", tier: "Rooted",       lastCheckIn: "5h ago",  streak: 21, resting: false, restingDays: 0, status: "thriving",  ringColor: "rgba(168,212,184,0.55)" },
  { name: "Hannah Doyle",   slug: "hannah",  initials: "HD", tier: "Rooted",       lastCheckIn: "Today",   streak: 12, resting: false, restingDays: 0, status: "steady",    ringColor: "rgba(127,119,221,0.55)" },
  { name: "Mara Whitfield", slug: "mara",    initials: "MW", tier: "Flourishing",  lastCheckIn: "Yesterday", streak: 56, resting: false, restingDays: 0, status: "steady",   ringColor: "rgba(160,120,32,0.55)" },
  { name: "Bethany Cole",   slug: "bethany", initials: "BC", tier: "Seedling",     lastCheckIn: "3d ago",  streak: 4,  resting: false, restingDays: 0, status: "needs love", ringColor: "rgba(192,120,74,0.55)" },
  { name: "Ruth Adler",     slug: "ruth",    initials: "RA", tier: "Rooted",       lastCheckIn: "1d ago",  streak: 17, resting: false, restingDays: 0, status: "steady",    ringColor: "rgba(168,212,184,0.55)" },
  { name: "Eden Marsh",     slug: "eden",    initials: "EM", tier: "Seedling",     lastCheckIn: "4d ago",  streak: 0,  resting: true,  restingDays: 4, status: "needs love", ringColor: "rgba(127,119,221,0.55)" },
];

/* ------------------------------ Sister row ------------------------------- */
function SisterRow({ s, isLast }: { s: Sister; isLast: boolean }) {
  const userId = `u_${s.slug}`;
  const season = useStoreQuery(() => queries.seasonState(userId));
  return (
    <div
      className="flex items-center gap-3 p-3.5 transition-all hover-elevate"
      style={{ borderBottom: !isLast ? "1px solid rgba(30,74,53,0.06)" : undefined }}
      data-testid={`sister-row-${s.initials.toLowerCase()}`}
    >
      <Link href={`/chat/${s.slug}`} className="no-underline flex items-center gap-3 flex-1 min-w-0" style={{ cursor: "pointer" }}>
        <Avatar initials={s.initials} size={42} ringColor={s.ringColor} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="mnf-body truncate" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--mnf-fd)" }}>{s.name}</p>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: statusStyle[s.status].dot, flexShrink: 0 }} />
          </div>
          <div className="flex items-center gap-1.5 mt-1" style={{ minWidth: 0, overflow: "hidden" }}>
            <span className="mnf-body shrink-0" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>{s.tier}</span>
            <span className="mnf-body shrink-0" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>·</span>
            <span className="shrink-0"><StreakInline streak={s.streak} resting={s.resting} restingDays={s.restingDays} /></span>
            {!s.resting && (
              <>
                <span className="mnf-body shrink-0" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>·</span>
                <span className="mnf-body truncate" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>{s.lastCheckIn}</span>
              </>
            )}
          </div>
          {season && season.kind !== "none" && (
            <div className="mt-1.5">
              <SeasonChip season={season} size="sm" testId={`chip-coach-season-${s.slug}`} />
            </div>
          )}
        </div>
      </Link>
      <Link
        href={`/coach/macros/${userId}`}
        className="no-underline shrink-0 flex items-center gap-1 hover-elevate"
        data-testid={`button-coach-macros-${s.slug}`}
        style={{
          padding: "6px 10px",
          borderRadius: 999,
          background: "rgba(168,212,184,0.18)",
          color: "var(--mnf-f)",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        <Icon name="local_fire_department" size={12} />
        Macros
      </Link>
      <span
        className="mnf-body shrink-0"
        style={{
          padding: "4px 10px", borderRadius: 999,
          background: statusStyle[s.status].bg,
          color: statusStyle[s.status].color,
          fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
        }}
      >
        {s.status}
      </span>
    </div>
  );
}

/* ---------------------------- KPI organic card --------------------------- */
function KpiCard({
  eyebrow, value, unit, sub, accent,
}: { eyebrow: string; value: string; unit?: string; sub: string; accent: string }) {
  return (
    <GlassCard organic padding={16}>
      <p className="mnf-eyebrow" style={{ color: accent }}>{eyebrow}</p>
      <p className="mnf-serif" style={{ fontSize: 32, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1, marginTop: 6 }}>
        {value}
        {unit && <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginLeft: 4, fontWeight: 500 }}>{unit}</span>}
      </p>
      <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 6, lineHeight: 1.4 }}>{sub}</p>
    </GlassCard>
  );
}

/* ----------------------- Weekly engagement bar chart --------------------- */
function EngagementChart() {
  const days = [
    { d: "M", v: 64 },
    { d: "T", v: 78 },
    { d: "W", v: 88 },
    { d: "T", v: 71 },
    { d: "F", v: 92 },
    { d: "S", v: 96 },
    { d: "S", v: 54 },
  ];
  const max = Math.max(...days.map((d) => d.v));
  const barAreaHeight = 100;
  return (
    <div className="flex items-stretch gap-2" style={{ height: 130 }}>
      {days.map((d, i) => {
        const h = Math.round((d.v / max) * barAreaHeight);
        const isToday = i === 5;
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end">
            <div className="w-full relative" style={{ height: barAreaHeight, display: "flex", alignItems: "flex-end" }}>
              <div
                style={{
                  width: "100%",
                  height: h,
                  background: isToday
                    ? "linear-gradient(180deg, var(--mnf-t) 0%, #A86638 100%)"
                    : "linear-gradient(180deg, rgba(30,74,53,0.85) 0%, rgba(30,74,53,0.5) 100%)",
                  borderRadius: "8px 8px 4px 4px",
                  position: "relative",
                }}
              >
                {isToday && (
                  <span
                    className="mnf-body absolute -top-5 left-1/2 -translate-x-1/2"
                    style={{ fontSize: 10, fontWeight: 700, color: "var(--mnf-t)" }}
                  >{d.v}</span>
                )}
              </div>
            </div>
            <span className="mnf-body" style={{ fontSize: 10, fontWeight: isToday ? 700 : 500, color: isToday ? "var(--mnf-t)" : "var(--mnf-tg)", marginTop: 6 }}>{d.d}</span>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------- Status colour map --------------------------- */
const statusStyle: Record<Status, { bg: string; color: string; dot: string }> = {
  "thriving":   { bg: "rgba(168,212,184,0.22)", color: "#2C6248",          dot: "#2C6248" },
  "steady":     { bg: "rgba(30,74,53,0.08)",    color: "var(--mnf-f)",     dot: "var(--mnf-f)" },
  "needs love": { bg: "rgba(192,120,74,0.16)",  color: "var(--mnf-t)",     dot: "var(--mnf-t)" },
};

/* --------------------------------- Page ---------------------------------- */
export default function Coach() {
  const [filter, setFilter] = useState<"all" | "needs love" | "thriving">("all");
  const filtered = filter === "all" ? SISTERS : SISTERS.filter((s) => s.status === filter);

  return (
    <div className="pb-6">
      <ScreenHeader
        variant="forest"
        eyebrow="Coach dashboard"
        title={<>Good evening,<br /><span className="italic">Coach Kiley</span></>}
        subtitle="7 sisters in your garden today. 3 need a gentle word."
        trailing={
          <Avatar initials="K" size={38} ringColor="rgba(229,185,151,0.65)" />
        }
      >
        <div className="flex items-center gap-2 flex-wrap">
          <PillTag tone="gold">12 active</PillTag>
          <PillTag tone="coral">3 to encourage</PillTag>
          <PillTag tone="fiber">5 wins this week</PillTag>
          <span
            className="inline-flex items-center gap-1"
            data-testid="badge-kiley-ggs-coach"
            style={{
              padding: "3px 8px", borderRadius: 999,
              background: "rgba(229,185,151,0.20)",
              border: "1px solid rgba(229,185,151,0.40)",
              color: "#E5B997",
              fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
            }}
          >
            <Icon name="verified" size={10} />
            GGS Certified
          </span>
        </div>
      </ScreenHeader>

      <div className="px-5 -mt-3 space-y-4">
        {/* Compliance dashboard CTA */}
        <Link href="/coach/compliance" className="no-underline block" data-testid="button-open-compliance">
          <GlassCard
            organic
            padding={16}
            className="hover-elevate transition-all"
            style={{
              background: "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(229,185,151,0.18) 100%)",
              cursor: "pointer",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 42, height: 42, borderRadius: 14,
                  background: "rgba(255,255,255,0.65)", color: "var(--mnf-f)",
                }}
              >
                <Icon name="insights" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>This week's tending</p>
                <p
                  className="mnf-serif"
                  style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 2 }}
                >
                  Open compliance view
                </p>
                <p
                  className="mnf-body"
                  style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.4 }}
                >
                  Macro adherence, prayer engagement, and who needs love today.
                </p>
              </div>
              <Icon name="chevron_right" size={18} />
            </div>
          </GlassCard>
        </Link>

        {/* Send a note CTA */}
        <Link href="/coach/notifications" className="no-underline block" data-testid="button-open-coach-notifications">
          <GlassCard
            organic
            padding={16}
            className="hover-elevate transition-all"
            style={{
              background: "linear-gradient(135deg, rgba(192,120,74,0.18) 0%, rgba(160,120,32,0.16) 100%)",
              cursor: "pointer",
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 42, height: 42, borderRadius: 14,
                  background: "rgba(255,255,255,0.65)", color: "var(--mnf-f)",
                }}
              >
                <Icon name="notifications_active" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)" }}>A note for the sisters</p>
                <p
                  className="mnf-serif"
                  style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 2 }}
                >
                  Send a notification
                </p>
                <p
                  className="mnf-body"
                  style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.4 }}
                >
                  Scripture, encouragement, or a class reminder. Pick who hears it.
                </p>
              </div>
              <Icon name="chevron_right" size={18} />
            </div>
          </GlassCard>
        </Link>

        {/* KPI grid */}
        <div className="grid grid-cols-2 gap-3">
          <KpiCard eyebrow="Active sisters"   value="12"   sub="3 new this month"          accent="var(--mnf-f)" />
          <KpiCard eyebrow="Wins this week"   value="28"   sub="goals + milestones logged" accent="var(--mnf-t)" />
          <KpiCard eyebrow="Avg streak"       value="22"   unit="days" sub="up 14% vs last week" accent="var(--mnf-purple)" />
          <KpiCard eyebrow="Retention"        value="94"   unit="%" sub="rolling 90 days" accent="var(--mnf-gold)" />
        </div>

        {/* Today's coaching queue */}
        {(() => {
          const QUEUE = [
            { type: "prayer",    name: "Bethany", slug: "bethany", body: "Asked for prayer · anxious about a job interview.",     icon: "favorite",     color: "var(--mnf-t)",     bg: "rgba(192,120,74,0.10)", action: "Reply" },
            { type: "missed",    name: "Eden",    slug: "eden",    body: "Resting · 4 days. Last mood: Restoring.",            icon: "schedule",     color: "var(--mnf-purple)", bg: "rgba(127,119,221,0.10)", action: "Reach out" },
            { type: "milestone", name: "Mara",    slug: "mara",    body: "56-day streak. First time hitting Flourishing tier.", icon: "celebration",  color: "var(--mnf-gold)",   bg: "rgba(160,120,32,0.10)", action: "Celebrate" },
          ];
          return (
          <>
        <SectionTitle eyebrow="Today's queue" title="Tend the garden" trailing={<span style={{ color: "var(--mnf-t)" }}>{QUEUE.length} items</span>} />
        {QUEUE.length === 0 ? (
          <GlassCard organic padding={28} style={{ textAlign: "center" }} data-testid="empty-queue">
            <span style={{ fontSize: 38, display: "block" }} aria-hidden>🌼</span>
            <p
              className="mnf-serif"
              style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 8 }}
            >
              The garden is quiet today
            </p>
            <p
              className="mnf-body italic"
              style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 6, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}
            >
              "He makes me lie down in green pastures." Take this rest, your sisters are tending themselves well.
            </p>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)", marginTop: 10 }}>Psalm 23:2</p>
          </GlassCard>
        ) : (
        <GlassCard padding={0} style={{ overflow: "hidden" }}>
          {QUEUE.map((q, i, arr) => (
            <div key={i} className="flex items-start gap-3 p-4" style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(30,74,53,0.06)" : undefined, background: q.bg }}>
              <div className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 12, background: "#fff", color: q.color }}>
                <Icon name={q.icon} size={18} weight={500} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>{q.name}</p>
                  <span className="mnf-eyebrow" style={{ color: q.color, fontSize: 9 }}>{q.type}</span>
                </div>
                <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.45, marginTop: 2 }}>{q.body}</p>
              </div>
              <Link href={`/chat/${q.slug}`} className="no-underline shrink-0">
                <button
                  data-testid={`button-coach-action-${i}`}
                  className="shrink-0 mnf-body transition-all hover-elevate"
                  style={{
                    padding: "7px 12px", borderRadius: 999,
                    background: q.color, color: "#fff",
                    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                    border: "none", cursor: "pointer",
                  }}
                >
                  {q.action}
                </button>
              </Link>
            </div>
          ))}
        </GlassCard>
        )}
          </>
          );
        })()}

        {/* Sister roster */}
        <SectionTitle eyebrow="The roster" title="Your sisters" trailing={<span style={{ color: "var(--mnf-t)" }}>{filtered.length} of {SISTERS.length}</span>} />
        <div className="flex items-center gap-2 flex-wrap">
          {(["all", "needs love", "thriving"] as const).map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.replace(" ", "-")}`}
              onClick={() => setFilter(f)}
              className="mnf-body transition-all"
              style={{
                padding: "7px 14px", borderRadius: 999,
                background: filter === f ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                color: filter === f ? "#F5F0E8" : "var(--mnf-f)",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                border: "1px solid transparent",
                cursor: "pointer",
              }}
            >
              {f === "all" ? "All sisters" : f}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <GlassCard organic padding={28} style={{ textAlign: "center" }} data-testid="empty-roster">
            <span style={{ fontSize: 36, display: "block" }} aria-hidden>🌿</span>
            <p
              className="mnf-serif"
              style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", marginTop: 8 }}
            >
              No sisters in this view
            </p>
            <p
              className="mnf-body italic"
              style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5, marginTop: 6, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}
            >
              "Be still and know." Adjust the filter to see who's blooming today.
            </p>
            <button
              onClick={() => setFilter("all")}
              data-testid="button-show-all"
              className="mnf-body transition-all hover-elevate"
              style={{
                marginTop: 14, padding: "8px 18px", borderRadius: 999,
                background: "var(--mnf-f)", color: "#F5F0E8",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                cursor: "pointer", border: "none",
              }}
            >
              Show all sisters
            </button>
          </GlassCard>
        ) : (
        <GlassCard padding={0} style={{ overflow: "hidden" }}>
          {filtered.map((s, i) => (
            <SisterRow key={s.name} s={s} isLast={i === filtered.length - 1} />
          ))}
        </GlassCard>
        )}

        {/* Engagement chart */}
        <SectionTitle eyebrow="This week" title="Community pulse" trailing={<span style={{ color: "var(--mnf-tg)" }}>logs / day</span>} />
        <GlassCard padding={20}>
          <div className="flex items-baseline gap-2 mb-4">
            <span className="mnf-serif" style={{ fontSize: 32, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1 }}>543</span>
            <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>logs total</span>
            <span className="ml-auto mnf-body" style={{ fontSize: 11, fontWeight: 700, color: "#2C6248", padding: "3px 9px", borderRadius: 999, background: "var(--mnf-fiberBg)", letterSpacing: "0.04em" }}>
              ↑ 18%
            </span>
          </div>
          <EngagementChart />
        </GlassCard>

        {/* Tier mix */}
        <SectionTitle eyebrow="Sanctuary" title="Tier mix" />
        <GlassCard padding={18}>
          <div className="space-y-4">
            {[
              { tier: "Seedling",    price: "$47",  count: 4, pct: 33, color: "#A8D4B8", text: "#2C6248" },
              { tier: "Rooted",      price: "$97",  count: 5, pct: 42, color: "var(--mnf-f)", text: "#F5F0E8" },
              { tier: "Flourishing", price: "$197", count: 3, pct: 25, color: "var(--mnf-t)", text: "#fff" },
            ].map((t) => (
              <div key={t.tier}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <div className="flex items-baseline gap-2">
                    <span className="mnf-serif" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)" }}>{t.tier}</span>
                    <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>{t.price}/mo</span>
                  </div>
                  <span className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-fd)" }}>
                    {t.count} <span className="mnf-body" style={{ fontWeight: 500, color: "var(--mnf-tg)" }}>sisters</span>
                  </span>
                </div>
                <div style={{ height: 8, borderRadius: 999, background: "rgba(30,74,53,0.06)", overflow: "hidden" }}>
                  <div style={{ width: `${t.pct}%`, height: "100%", background: t.color, borderRadius: 999 }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 flex items-baseline justify-between" style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}>
            <div>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Monthly recurring</p>
              <p className="mnf-serif" style={{ fontSize: 28, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1, marginTop: 4 }}>$1,264</p>
            </div>
            <Btn variant="secondary" size="sm">View sanctuary</Btn>
          </div>
        </GlassCard>

        {/* Live broadcasts */}
        <CoachLivePanel />

        {/* Sister inbox queue */}
        <CoachQueuePanel />

        {/* Quick actions */}
        <div className="grid grid-cols-2 gap-3">
          <Link href="/go-live" className="no-underline block w-full" data-testid="link-coach-go-live">
            <Btn variant="coral" block size="md">
              <Icon name="videocam" size={16} /> Go live
            </Btn>
          </Link>
          <Btn variant="primary" block size="md">
            <Icon name="campaign" size={16} /> Send word
          </Btn>
        </div>

        {/* Back to my view */}
        <Link href="/" className="no-underline block">
          <GlassCard padding={14} className="flex items-center gap-3">
            <div className="flex items-center justify-center shrink-0" style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)" }}>
              <Icon name="arrow_back" size={18} />
            </div>
            <div className="flex-1">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Back to</p>
              <p className="mnf-body" style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}>My garden view</p>
            </div>
            <Icon name="arrow_forward" size={18} weight={500} />
          </GlassCard>
        </Link>
      </div>
    </div>
  );
}

/* ------------------------- Coach inbox queue panel ------------------------ */
function CoachQueuePanel() {
  const queue = useStoreQuery(() => queries.coachQueue());

  const fmtWait = (ms: number) => {
    const m = Math.max(1, Math.round(ms / 60000));
    if (m < 60) return `${m} min`;
    const h = Math.round(m / 60);
    return `${h}h`;
  };
  const tierTone = (tier: string): "forest" | "fiber" | "coral" | "purple" | "neutral" | "gold" =>
    tier === "Flourishing" ? "gold" : tier === "Rooted" ? "forest" : "neutral";
  const routeFor = (threadId: string) => threadId === "kiley" ? "/kiley" : `/chat/${threadId}`;

  if (queue.length === 0) {
    return (
      <GlassCard organic padding={18} data-testid="card-coach-queue-empty">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Sister inbox</p>
        <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15, marginTop: 4 }}>
          Inbox at rest
        </p>
        <p className="mnf-body" style={{ fontSize: 12.5, color: "var(--mnf-tg)", marginTop: 8, lineHeight: 1.5 }}>
          Every sister has been answered. Sister Lydia is tending the Seedlings.
        </p>
      </GlassCard>
    );
  }

  return (
    <GlassCard organic padding={18} data-testid="card-coach-queue">
      <div className="flex items-center justify-between">
        <div>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Sister inbox</p>
          <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15, marginTop: 4 }}>
            {queue.length} waiting on you
          </p>
        </div>
        <PillTag tone="coral">FIFO</PillTag>
      </div>

      <div className="mt-4 space-y-2">
        {queue.map((row, i) => (
          <Link
            key={row.threadId}
            href={routeFor(row.threadId)}
            className="no-underline flex items-center gap-3"
            style={{
              padding: "10px 12px",
              borderRadius: 16,
              background: i === 0 ? "rgba(192,120,74,0.08)" : "rgba(30,74,53,0.04)",
              border: i === 0 ? "1px solid rgba(192,120,74,0.22)" : "1px solid rgba(30,74,53,0.06)",
            }}
            data-testid={`row-queue-${row.threadId}`}
          >
            <Avatar initials={row.initials} size={36} ringColor="rgba(192,120,74,0.55)" />
            <div className="flex-1" style={{ minWidth: 0 }}>
              <p className="mnf-body truncate" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mnf-fd)" }}>
                {row.name}
              </p>
              <p className="mnf-body truncate" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 2, lineHeight: 1.4 }}>
                {row.type === "voice" ? "Voice note" : row.type === "verse" ? "Scripture" : row.body}
              </p>
              <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                <span className="mnf-eyebrow" style={{ fontSize: 9, color: tierTone(row.tier) === "gold" ? "var(--mnf-gold)" : tierTone(row.tier) === "forest" ? "var(--mnf-f)" : "var(--mnf-tg)", letterSpacing: "0.08em" }}>
                  {row.tier}
                </span>
                <span style={{ width: 3, height: 3, borderRadius: 999, background: "var(--mnf-tg)", opacity: 0.4 }} />
                <span className="mnf-eyebrow" style={{ fontSize: 9, color: i === 0 ? "var(--mnf-t)" : "var(--mnf-tg)", letterSpacing: "0.08em" }}>
                  {fmtWait(row.awaitingSinceMs)} waiting
                </span>
              </div>
            </div>
            <div
              className="flex items-center justify-center shrink-0"
              style={{
                width: 34, height: 34, borderRadius: 999,
                background: i === 0 ? "var(--mnf-t)" : "rgba(30,74,53,0.08)",
                color: i === 0 ? "#F5F0E8" : "var(--mnf-fd)",
              }}
              aria-label="Reply"
              data-testid={`button-queue-reply-${row.threadId}`}
            >
              <Icon name="reply" size={16} />
            </div>
          </Link>
        ))}
      </div>

      <p className="mnf-body italic" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 12, lineHeight: 1.5 }}>
        "Bear one another's burdens, and so fulfill the law of Christ." Galatians 6:2
      </p>
    </GlassCard>
  );
}

// silence unused warning for mutations import (reserved for canned reply v2)
void mutations;

/* --------------------------- Live broadcasts panel ------------------------ */
function CoachLivePanel() {
  const stats = useStoreQuery(() => queries.coachLiveStats("u_kiley"));
  const live = useStoreQuery(queries.liveNow);
  const upcoming = useStoreQuery(queries.liveUpcoming);
  const nextLive = live[0];
  const nextUpcoming = upcoming[0];

  const fmtIn = (ms: number) => {
    const diff = ms - Date.now();
    if (diff < 0) return "now";
    const h = Math.round(diff / (1000 * 60 * 60));
    if (h < 1) return `${Math.max(1, Math.round(diff / (1000 * 60)))}m`;
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
  };

  return (
    <GlassCard organic padding={18} data-testid="card-coach-live-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Live broadcasts</p>
          <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15, marginTop: 4 }}>
            {nextLive ? "You're live with sisters" : "Your next gathering"}
          </p>
        </div>
        {nextLive && (
          <PillTag tone="coral">● {stats.nowWatching} watching</PillTag>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <CoachLiveStat label="Live now" value={stats.live} accent="var(--mnf-c)" />
        <CoachLiveStat label="Upcoming" value={stats.upcoming} accent="var(--mnf-p)" />
        <CoachLiveStat label="Replays" value={stats.replays} accent="var(--mnf-fb)" />
      </div>

      {(nextLive || nextUpcoming) && (
        <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(30,74,53,0.08)" }}>
          {nextLive ? (
            <Link
              href={`/live/${nextLive.id}`}
              className="no-underline flex items-center gap-3"
              data-testid="link-coach-resume-live"
            >
              <div
                className="flex items-center justify-center shrink-0"
                style={{ width: 38, height: 38, borderRadius: 12, background: "var(--mnf-c)", color: "#F5F0E8" }}
              >
                <Icon name="sensors" size={18} filled />
              </div>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-c)" }}>On air</p>
                <p className="mnf-body truncate" style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}>
                  {nextLive.title}
                </p>
              </div>
              <Icon name="arrow_forward" size={18} weight={500} />
            </Link>
          ) : nextUpcoming ? (
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center shrink-0"
                style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(127,119,221,0.18)", color: "var(--mnf-p)" }}
              >
                <Icon name="event" size={18} />
              </div>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-p)" }}>Up next, in {fmtIn(nextUpcoming.scheduledFor)}</p>
                <p className="mnf-body truncate" style={{ fontSize: 13, fontWeight: 600, color: "var(--mnf-fd)" }}>
                  {nextUpcoming.title}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </GlassCard>
  );
}

function CoachLiveStat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{
        padding: "10px 6px",
        borderRadius: 14,
        background: "rgba(30,74,53,0.05)",
      }}
    >
      <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: accent, lineHeight: 1 }}>
        {value}
      </p>
      <p className="mnf-eyebrow" style={{ marginTop: 4, color: "var(--mnf-tg)", fontSize: 9 }}>
        {label}
      </p>
    </div>
  );
}
